import { computed, type ComputedRef } from 'vue';
import type {
  ConditionCompareNode,
  ConditionListNode,
  ConditionLiteralNode,
  ConditionLogicalNode,
  ConditionNotNode,
  ConditionPathNode,
  TemplateField,
} from '@/api';

/**
 * Structured condition expressions. The backend emits these in
 * `TemplateField.condition_ast` instead of a stringified Python expression,
 * so the frontend doesn't need a Jinja parser — just a small tree walker.
 *
 * The shape mirrors `TemplateField.condition_ast` from the OpenAPI spec.
 */
export type ConditionAst =
  | ({ kind: 'path' } & ConditionPathNode)
  | ({ kind: 'literal' } & ConditionLiteralNode)
  | ({ kind: 'list' } & ConditionListNode)
  | ({ kind: 'compare' } & ConditionCompareNode)
  | ({ kind: 'logical' } & ConditionLogicalNode)
  | ({ kind: 'not' } & ConditionNotNode);

/**
 * Resolve a dot-path against an arbitrary object. Returns undefined when any
 * segment is missing or when the traversal hits a non-object — the caller
 * uses that distinction to fall through to the next enclosing scope.
 */
function resolveFormPath(data: unknown, path: string): unknown {
  const segments = path.split('.');
  let current: unknown = data;
  for (const segment of segments) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/**
 * Walk the scope chain innermost-out, then fall back to the form root. This
 * is the same lookup the validators and renderService use, so a path like
 * `"is_international"` resolves consistently whether the field lives at the
 * top level or inside a nested list item.
 */
function lookupPath(path: string, localScopes: unknown[], rootFormData: unknown): unknown {
  for (const scope of localScopes) {
    const resolved = resolveFormPath(scope, path);
    if (resolved !== undefined) return resolved;
  }
  return resolveFormPath(rootFormData, path);
}

function containsMember(left: unknown, right: unknown): boolean {
  if (Array.isArray(right)) return right.includes(left);
  // Python-ish substring `in`: `"foo" in "foobar"`.
  if (typeof left === 'string' && typeof right === 'string') return right.includes(left);
  return false;
}

/**
 * Tree walker — returns the *value* of a node so `compare`/`logical`/`not`
 * can compose. Booleans are produced by `compare` / `logical` / `not`;
 * `path` returns the form-data value; `literal` returns its `value`; `list`
 * returns a real JS array.
 */
function evalNode(node: ConditionAst, localScopes: unknown[], rootFormData: unknown): unknown {
  switch (node.kind) {
    case 'literal':
      return node.value ?? null;
    case 'path':
      return lookupPath(node.path, localScopes, rootFormData);
    case 'list':
      return (node.items ?? []).map(item => evalNode(item, localScopes, rootFormData));
    case 'compare': {
      const left = evalNode(node.left, localScopes, rootFormData);
      const right = evalNode(node.right, localScopes, rootFormData);
      switch (node.op) {
        case '==':
          return left === right;
        case '!=':
          return left !== right;
        case '<':
          return Number(left) < Number(right);
        case '<=':
          return Number(left) <= Number(right);
        case '>':
          return Number(left) > Number(right);
        case '>=':
          return Number(left) >= Number(right);
        case 'in':
          return containsMember(left, right);
        case 'not in':
          return !containsMember(left, right);
      }
      return false;
    }
    case 'logical': {
      const left = Boolean(evalNode(node.left, localScopes, rootFormData));
      // Short-circuit semantics, matching how the backend's Jinja would
      // evaluate the expression.
      if (node.op === 'or') {
        return left || Boolean(evalNode(node.right, localScopes, rootFormData));
      }
      return left && Boolean(evalNode(node.right, localScopes, rootFormData));
    }
    case 'not':
      return !Boolean(evalNode(node.operand, localScopes, rootFormData));
  }
}

/**
 * Evaluate a schema field's `condition_ast`. Returns true on an absent AST
 * (no gate) and on evaluation errors — the field is shown rather than
 * silently hidden, matching the principle of least surprise.
 *
 * `localScopes` is the chain of enclosing scopes from innermost to outermost
 * (e.g. `[milestone, project]` inside a nested list).
 */
export function evaluateCondition(
  ast: ConditionAst | null | undefined,
  localScopes: unknown[],
  rootFormData: unknown
): boolean {
  if (!ast) return true;
  try {
    return Boolean(evalNode(ast, localScopes, rootFormData));
  } catch {
    return false;
  }
}

/**
 * Reactive visibility check for a schema field. Re-evaluates whenever the
 * root reactive form state or any enclosing scope changes.
 */
export function useFieldVisibility(
  field: () => TemplateField,
  rootFormData: () => Record<string, unknown>,
  localScopes?: () => unknown[]
): ComputedRef<boolean> {
  return computed(() => {
    const ast = field().condition_ast;
    return evaluateCondition(ast, localScopes?.() ?? [], rootFormData());
  });
}
